import {
    getGroupAndTreeId,
    getGroupId,
    getScheduleId,
    getScheduleRecordId,
    isGlobalTailoringId,
} from "library/get-unique-id-from-tree.mjs"
import { getTreeId } from "library/id-helpers.mjs"
import { useUndo } from "lib/undo"
import { useCallback, useEffect, useMemo, useRef, useState } from "react"
import LoadingScreen from "minimals-template/components/LoadingScreen"
import { Bound } from "lib/@components/binding/Bound"
import { SaveIndicator } from "lib/@components/save-indicator/save-indicator"
import { UndoLive } from "routes/schedule/undo-portal"
import { Lock } from "minimals-template/components/lock/Lock"
import { saveRegimeTailoringPayload } from "routes/schedule/controller/save-regime-tailoring-payload"
import { ScheduleExtension } from "routes/schedule/extendedSchedule"
import { Undo } from "routes/schedule/plugins/undo-redo/undo"
import { useOfflineSave } from "lib/@offline/use-offline-save"
import { registerSaveFunction, unregisterSaveFunction } from "lib/@offline/draft-sync-manager"
import { getVersionFromSaveResult } from "routes/schedule/edit/version-from-save-result"

export function EditScheduleTailoring({
    payload,
    id,
    item,
    children,
    onConfigureStorage = (v) => v,
    storePayloadFunction,
    draftKey: draftKeyProp,
    editorType: editorTypeProp,
}) {
    const globalTailoring = isGlobalTailoringId(id, true)

    // Derive draftKey and editorType from props or from the id
    const draftKey = draftKeyProp || `tailoring:${getTreeId(id)}:${getGroupId(id)}:${getScheduleId(id)}`
    const editorType = editorTypeProp || "regime-tailoring"

    const lastTreeVersionRef = useRef(null)

    // Context stored alongside the draft so fallback save functions can
    // reconstruct the save call without the editor being mounted.
    // Not memoized — expectedTreeVersion changes after each successful save
    // and saveContextRef in useOfflineSave captures the latest value at write time.
    const saveContext = {
        treeId: getTreeId(id),
        groupId: getGroupId(id),
        scheduleId: getScheduleId(id),
        scheduleRecordId: getScheduleRecordId(id),
        groupAndTreeId: getGroupAndTreeId(id),
        globalTailoring,
        id,
        expectedTreeVersion: lastTreeVersionRef.current,
    }

    // When a queued local draft is restored, use it as the initialTarget instead
    // of the server payload. This reinitialises the undo buffer cleanly rather
    // than mutating the existing target in-place (which would corrupt undo history).
    const [restoredDraft, setRestoredDraft] = useState(null)

    const [undoablePayload, { onChange, ...props }] = useUndo({
        ref: item,
        initialTarget: restoredDraft ?? payload,
        change: (item) => {
            if (!item) storePayload()
        },
    })

    const saveFn = useMemo(() => storePayloadFunction || store, [storePayloadFunction, id]) // eslint-disable-line react-hooks/exhaustive-deps

    // eslint-disable-next-line react-hooks/exhaustive-deps
    const { onChange: storePayload } = useOfflineSave({
        id: "tailor",
        save: saveFn,
        target: undoablePayload,
        draftKey,
        editorType,
        serverVersion: payload?._draftVersion ?? payload?.version,
        wait: 1000,
        saveContext,
        onRestore: handleRestoredDraft,
    })

    // Register save function for the sync manager keyed by draftKey so it can
    // retry the correct schedule-specific handler when connectivity returns
    useEffect(() => {
        registerSaveFunction(draftKey, saveFn)
        return () => unregisterSaveFunction(draftKey)
    }, [draftKey, saveFn])

    const updateAndStore = useCallback(() => {
        onChange()
        storePayload()
    }, [onChange, storePayload])

    // eslint-disable-next-line react-hooks/exhaustive-deps
    const finalSave = useMemo(() => onConfigureStorage(updateAndStore, storePayloadFunction ?? store), [updateAndStore])
    const modifiedStore = useCallback(() => {
        finalSave(undoablePayload)
        return false
    }, [finalSave, undoablePayload])

    return !payload ? (
        <LoadingScreen description="Has Payload" />
    ) : (
        <Bound
            {...props}
            currentId={id}
            currentGroup={item}
            isExtended={true}
            payload={undoablePayload}
            editExtension={true}
            showAll={true}
            storePayload={modifiedStore}
            parentItem={item}
        >
            <SaveIndicator id="tailor" />
            <UndoLive>
                <Bound editMode={true}>
                    <Undo />
                </Bound>
            </UndoLive>
            <ScheduleExtension.Wrapper>
                <Lock id={id}>{children}</Lock>
            </ScheduleExtension.Wrapper>
        </Bound>
    )

    function handleRestoredDraft(restoredData, draft) {
        const restoredTreeVersion = draft?.saveContext?.expectedTreeVersion ?? draft?.serverVersion
        if (restoredTreeVersion != null) {
            lastTreeVersionRef.current = restoredTreeVersion
        }
        setRestoredDraft(restoredData)
    }

    async function store(payload, options = {}) {
        const result = await saveRegimeTailoringPayload(
            payload,
            {
                ...saveContext,
                expectedTreeVersion: lastTreeVersionRef.current,
            },
            options
        )
        const nextTreeVersion = getVersionFromSaveResult(result, "treeVersion")
        if (!result?.conflict && nextTreeVersion != null) lastTreeVersionRef.current = nextTreeVersion
        return result
    }
}
